import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BarChart3, Download, FileText, ExternalLink, Filter, ArrowUpDown, User, Search, Mail, X } from "lucide-react";
import SharedDateField from "@/components/app/DateField";

const COLUMNS = [
  { key: "player_name", label: "Jugador", type: "text" },
  { key: "team_name", label: "Equipo", type: "text" },
  { key: "category_name", label: "Categoría", type: "text" },
  { key: "position", label: "Pos.", type: "text", short: true },
  { key: "partidos", label: "PJ", type: "num" },
  { key: "minutos", label: "Min", type: "num" },
  { key: "gols", label: "G", type: "num" },
  { key: "asistencias", label: "A", type: "num" },
  { key: "gols_por_90", label: "G/90", type: "num" },
  { key: "targetes_grogues", label: "TA", type: "num" },
  { key: "targetes_roges", label: "TR", type: "num" },
  { key: "avg_rating", label: "Rating", type: "num" },
  { key: "pct_asistencia", label: "% Asist.", type: "num" },
  { key: "pct_convocatoria", label: "% Conv.", type: "num" },
];

export default function StatsPage() {
  const [categories, setCategories] = useState([]);
  const [teams, setTeams] = useState([]);
  const [filters, setFilters] = useState({ category_id: "", team_id: "", player_id: "", date_from: "", date_to: "", season: "" });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState("player_name");
  const [sortDir, setSortDir] = useState("asc");
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [c, t] = await Promise.all([api.get("/categories"), api.get("/teams")]);
        setCategories(c.data);
        setTeams(t.data);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, []);

  const filteredTeams = useMemo(
    () => (filters.category_id ? teams.filter(t => t.category_id === filters.category_id) : teams),
    [teams, filters.category_id]
  );

  const seasonsList = useMemo(() => {
    const s = new Set();
    teams.forEach(t => { if (t.season) s.add(t.season); });
    return Array.from(s).sort().reverse();
  }, [teams]);

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filters.category_id) q.set("category_id", filters.category_id);
      if (filters.team_id) q.set("team_id", filters.team_id);
      if (filters.player_id) q.set("player_id", filters.player_id);
      if (filters.date_from) q.set("date_from", filters.date_from);
      if (filters.date_to) q.set("date_to", filters.date_to);
      if (filters.season) q.set("season", filters.season);
      const { data } = await api.get(`/stats/players?${q.toString()}`);
      setRows(data.rows);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* first load + refetch on filter change */ }, [filters]);

  const sorted = useMemo(() => {
    const term = search.trim().toLowerCase();
    let copy = [...rows];
    if (term) {
      copy = copy.filter(r =>
        (r.player_name || "").toLowerCase().includes(term) ||
        String(r.jersey_number || "").includes(term) ||
        (r.team_name || "").toLowerCase().includes(term)
      );
    }
    copy.sort((a, b) => {
      const va = a[sortKey]; const vb = b[sortKey];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return sortDir === "asc" ? va - vb : vb - va;
      return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
    return copy;
  }, [rows, sortKey, sortDir, search]);

  const totals = useMemo(() => {
    const t = { partidos: 0, minutos: 0, gols: 0, asistencias: 0, targetes_grogues: 0, targetes_roges: 0 };
    rows.forEach(r => {
      t.partidos += r.partidos; t.minutos += r.minutos; t.gols += r.gols;
      t.asistencias += r.asistencias; t.targetes_grogues += r.targetes_grogues; t.targetes_roges += r.targetes_roges;
    });
    return t;
  }, [rows]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir(k === "player_name" || k === "team_name" || k === "category_name" ? "asc" : "desc"); }
  };

  const exportCSV = async () => {
    try {
      const q = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) q.set(k, v); });
      const res = await api.get(`/stats/players.csv?${q.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sudeporte-estadisticas-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportTeamPDF = async () => {
    if (!filters.team_id) { toast.error("Selecciona un equipo en los filtros para generar su informe"); return; }
    try {
      const q = filters.season ? `?season=${encodeURIComponent(filters.season)}` : "";
      const res = await api.get(`/reports/team/${filters.team_id}/pdf${q}`, { responseType: "blob" });
      const teamName = teams.find(t => t.id === filters.team_id)?.name || "equipo";
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `Informe_Equipo_${teamName.replace(/ /g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Informe de equipo descargado");
    } catch { toast.error("No se pudo generar el informe del equipo"); }
  };

  const [sendOpen, setSendOpen] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [checked, setChecked] = useState({});
  const [extraEmails, setExtraEmails] = useState("");
  const [sending, setSending] = useState(false);

  const openSendModal = async () => {
    if (!filters.team_id) { toast.error("Selecciona un equipo en los filtros primero"); return; }
    setSendOpen(true); setExtraEmails("");
    try {
      const { data } = await api.get(`/reports/team/${filters.team_id}/recipients`);
      setRecipients(data);
      setChecked(Object.fromEntries(data.map(r => [r.email, true])));
    } catch { setRecipients([]); setChecked({}); }
  };

  const doSendTeamPDF = async () => {
    const selected = recipients.filter(r => checked[r.email]).map(r => r.email);
    const extras = extraEmails.split(/[,;\s]+/).map(e => e.trim()).filter(e => e.includes("@"));
    const emails = [...new Set([...selected, ...extras])];
    if (emails.length === 0) { toast.error("Selecciona o escribe al menos un email"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/reports/team/${filters.team_id}/send`, {
        emails, season: filters.season || null,
      });
      if (data.failed?.length) toast.warning(`Enviado a ${data.sent}. Fallaron: ${data.failed.join(", ")}`);
      else toast.success(`Informe enviado a ${data.sent} destinatario${data.sent === 1 ? "" : "s"}`);
      setSendOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="overline">Datos consolidados</div>
          <h1 className="font-heading font-black text-5xl tracking-tighter uppercase mt-2">Estadísticas</h1>
          <p className="text-muted-foreground text-sm mt-2 max-w-2xl">
            Rendimiento por jugador con filtros combinables por categoría, equipo, jugador y fechas. Exporta a CSV o descarga PDF individual.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="stats-team-pdf-btn" onClick={exportTeamPDF} disabled={!filters.team_id}
                  title={filters.team_id ? "Descargar informe PDF del equipo" : "Selecciona un equipo en los filtros"}
                  className="inline-flex items-center gap-2 h-10 px-4 border border-primary/60 text-primary hover:bg-primary/10 text-xs uppercase font-heading tracking-wider transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            <FileText size={14} /> PDF Equipo
          </button>
          <button data-testid="stats-team-send-btn" onClick={openSendModal} disabled={!filters.team_id}
                  title={filters.team_id ? "Enviar informe PDF del equipo por email" : "Selecciona un equipo en los filtros"}
                  className="inline-flex items-center gap-2 h-10 px-4 border border-primary/60 text-primary hover:bg-primary/10 text-xs uppercase font-heading tracking-wider transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            <Mail size={14} /> Enviar PDF
          </button>
          <button data-testid="stats-export-csv-btn" onClick={exportCSV}
                  className="inline-flex items-center gap-2 h-10 px-4 border border-primary/60 text-primary hover:bg-primary/10 text-xs uppercase font-heading tracking-wider transition-colors">
            <Download size={14} /> Exportar CSV
          </button>
        </div>
      </div>

      {sendOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card border border-border w-full max-w-md p-6 space-y-4" data-testid="send-team-pdf-modal">
            <div className="flex items-start justify-between">
              <div>
                <div className="overline">Enviar informe de equipo</div>
                <div className="font-heading font-black text-xl uppercase tracking-tight mt-1">
                  {teams.find(t => t.id === filters.team_id)?.name || "—"}
                </div>
                <div className="text-[10px] font-mono text-muted-foreground mt-1">
                  {filters.season ? `Temporada ${filters.season}` : "Todos los registros"} · PDF adjunto
                </div>
              </div>
              <button onClick={() => setSendOpen(false)} data-testid="send-team-pdf-close" className="p-1 hover:text-primary"><X size={18} /></button>
            </div>
            <div>
              <div className="overline mb-2">Coordinación del club</div>
              {recipients.length === 0 && <div className="text-xs text-muted-foreground">No hay coordinadores con email en el club.</div>}
              <div className="space-y-1 max-h-44 overflow-y-auto">
                {recipients.map(r => (
                  <label key={r.email} className="flex items-center gap-3 p-2 border border-border/60 cursor-pointer hover:border-primary/50" data-testid={`send-recipient-${r.email}`}>
                    <input type="checkbox" checked={!!checked[r.email]} onChange={() => setChecked(c => ({ ...c, [r.email]: !c[r.email] }))} className="accent-[#e11d2e]" />
                    <div className="min-w-0">
                      <div className="text-xs font-semibold truncate">{r.name}</div>
                      <div className="text-[10px] font-mono text-muted-foreground truncate">{r.email} · {r.role === "club_admin" ? "Admin" : r.role === "director_deportivo" ? "Dir. Deportivo" : "Coordinador"}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="overline block mb-1">Otros emails (separados por coma)</label>
              <input type="text" data-testid="send-extra-emails" value={extraEmails} onChange={(e) => setExtraEmails(e.target.value)}
                     placeholder="direccion@club.com, otro@email.com"
                     className="w-full h-10 bg-transparent border border-border px-3 font-mono text-xs focus:border-primary focus:outline-none" />
            </div>
            <div className="flex items-center justify-end gap-3 pt-1">
              <button onClick={() => setSendOpen(false)} className="h-10 px-4 border border-border text-xs uppercase font-heading tracking-wider">Cancelar</button>
              <button data-testid="send-team-pdf-confirm" onClick={doSendTeamPDF} disabled={sending} className="btn-volt disabled:opacity-50">
                <Mail size={14} /> {sending ? "Enviando…" : "Enviar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="border border-border p-4 mb-4 bg-white/[0.02]">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={13} className="text-primary" /><span className="overline">Filtros</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <SelectField label="Temporada" tid="stats-filter-season" value={filters.season}
                       onChange={(v) => setFilters(f => ({ ...f, season: v, date_from: "", date_to: "" }))}>
            <option value="">Todas</option>
            {seasonsList.map(s => <option key={s} value={s}>{s}</option>)}
          </SelectField>
          <SelectField label="Categoría" tid="stats-filter-category" value={filters.category_id}
                       onChange={(v) => setFilters(f => ({ ...f, category_id: v, team_id: "", player_id: "" }))}>
            <option value="">Todas</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </SelectField>
          <SelectField label="Equipo" tid="stats-filter-team" value={filters.team_id}
                       onChange={(v) => setFilters(f => ({ ...f, team_id: v, player_id: "" }))}>
            <option value="">Todos</option>
            {filteredTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </SelectField>
          <SelectField label="Jugador" tid="stats-filter-player" value={filters.player_id}
                       onChange={(v) => setFilters(f => ({ ...f, player_id: v }))}>
            <option value="">Todos</option>
            {(rows || []).filter(r => !filters.team_id || r.team_id === filters.team_id).map(p => (
              <option key={p.player_id} value={p.player_id}>{p.player_name}</option>
            ))}
          </SelectField>
          <DateField label="Desde" tid="stats-filter-from" value={filters.date_from}
                     onChange={(v) => setFilters(f => ({ ...f, date_from: v, season: "" }))} />
          <DateField label="Hasta" tid="stats-filter-to" value={filters.date_to}
                     onChange={(v) => setFilters(f => ({ ...f, date_to: v, season: "" }))} />
          <div className="flex items-end">
            <button
              data-testid="stats-clear-filters-btn"
              onClick={() => { setFilters({ category_id: "", team_id: "", player_id: "", date_from: "", date_to: "", season: "" }); setSearch(""); }}
              className="w-full h-10 border border-border hover:border-primary hover:text-primary text-xs uppercase font-heading tracking-wider transition-colors"
            >Limpiar</button>
          </div>
        </div>
        {/* Quick search across displayed rows */}
        <div className="mt-3 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="stats-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar rápido en la tabla por nombre, dorsal o equipo…"
            className="w-full h-10 pl-9 pr-3 bg-card border border-border text-sm focus:outline-none focus:border-primary transition-colors"
          />
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
        <KPI label="Jugadores" value={rows.length} />
        <KPI label="Partidos" value={totals.partidos} />
        <KPI label="Minutos" value={totals.minutos} />
        <KPI label="Goles" value={totals.gols} />
        <KPI label="Asist." value={totals.asistencias} />
        <KPI label="T.Amar/Roj" value={`${totals.targetes_grogues}/${totals.targetes_roges}`} />
      </div>

      {/* Table */}
      <div className="border border-border overflow-x-auto" data-testid="stats-table">
        <table className="w-full text-xs">
          <thead className="bg-white/[0.03] border-b border-border">
            <tr>
              {COLUMNS.map(col => (
                <th key={col.key}
                    onClick={() => toggleSort(col.key)}
                    data-testid={`stats-col-${col.key}`}
                    className={`px-3 py-2 overline text-[10px] cursor-pointer select-none hover:text-primary ${col.type === "num" ? "text-right" : "text-left"}`}>
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    <ArrowUpDown size={9} className={sortKey === col.key ? "text-primary" : "text-muted-foreground/40"} />
                  </span>
                </th>
              ))}
              <th className="px-3 py-2 overline text-[10px] text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={COLUMNS.length + 1} className="p-8 text-center text-muted-foreground">
                <BarChart3 size={28} className="mx-auto mb-2 opacity-40" /> Calculando…
              </td></tr>
            )}
            {!loading && sorted.length === 0 && (
              <tr><td colSpan={COLUMNS.length + 1} className="p-8 text-center text-muted-foreground">
                Sin datos para los filtros seleccionados.
              </td></tr>
            )}
            {!loading && sorted.map(r => (
              <tr key={r.player_id} data-testid={`stats-row-${r.player_id}`}
                  className="border-b border-border/40 hover:bg-white/[0.02] transition-colors">
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    {r.jersey_number != null && (
                      <span className="w-6 h-6 border border-border font-heading font-bold text-[10px] flex items-center justify-center shrink-0">{r.jersey_number}</span>
                    )}
                    <Link to={`/player/${r.player_id}`} className="font-heading font-semibold uppercase hover:text-primary transition-colors truncate max-w-[180px]" title={r.player_name}>
                      {r.player_name}
                    </Link>
                  </div>
                </td>
                <td className="px-3 py-2 text-muted-foreground truncate max-w-[120px]" title={r.team_name}>{r.team_name}</td>
                <td className="px-3 py-2 text-muted-foreground truncate max-w-[120px]" title={r.category_name}>{r.category_name}</td>
                <td className="px-3 py-2 font-mono text-[10px]">{r.position || "—"}</td>
                <td className="px-3 py-2 font-mono text-right">{r.partidos}</td>
                <td className="px-3 py-2 font-mono text-right">{r.minutos}</td>
                <td className="px-3 py-2 font-mono text-right font-bold text-primary">{r.gols}</td>
                <td className="px-3 py-2 font-mono text-right">{r.asistencias}</td>
                <td className="px-3 py-2 font-mono text-right">{r.gols_por_90}</td>
                <td className="px-3 py-2 font-mono text-right text-amber-400">{r.targetes_grogues}</td>
                <td className="px-3 py-2 font-mono text-right text-red-400">{r.targetes_roges}</td>
                <td className="px-3 py-2 font-mono text-right">{r.avg_rating}</td>
                <td className="px-3 py-2 font-mono text-right">{r.pct_asistencia}%</td>
                <td className="px-3 py-2 font-mono text-right">{r.pct_convocatoria}%</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Link to={`/player/${r.player_id}`}
                        data-testid={`stats-view-${r.player_id}`}
                        className="inline-flex items-center gap-1 h-7 px-2 border border-border hover:border-primary hover:text-primary text-[10px] uppercase transition-colors mr-1"
                        title="Ver ficha">
                    <User size={10} />
                  </Link>
                  <PDFButton playerId={r.player_id} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KPI({ label, value }) {
  const tid = `kpi-${label.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;
  return (
    <div className="border border-border p-3" data-testid={tid}>
      <div className="overline text-[10px]">{label}</div>
      <div className="font-heading font-black text-2xl mt-1" data-testid={`${tid}-value`}>{value}</div>
    </div>
  );
}

function SelectField({ label, value, onChange, children, tid }) {
  return (
    <div>
      <label className="overline block mb-1 text-[10px]">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={tid}
              className="w-full h-10 bg-card border border-border px-3 font-mono text-xs focus:border-primary focus:outline-none">
        {children}
      </select>
    </div>
  );
}

function DateField({ label, value, onChange, tid }) {
  return (
    <div>
      <label className="overline block mb-1 text-[10px]">{label}</label>
      <SharedDateField value={value} onChange={onChange} testid={tid} />
    </div>
  );
}

function PDFButton({ playerId }) {
  const [downloading, setDownloading] = useState(false);
  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/reports/player/${playerId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `informe-jugador-${playerId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDownloading(false); }
  };
  return (
    <button
      data-testid={`stats-pdf-${playerId}`}
      onClick={download}
      disabled={downloading}
      className="inline-flex items-center gap-1 h-7 px-2 border border-border hover:border-primary hover:text-primary text-[10px] uppercase transition-colors disabled:opacity-50"
      title="Descargar PDF"
    >
      <FileText size={10} />
    </button>
  );
}
